<script setup>
import { getCurrentInstance, computed } from "vue";
import { useStore } from "vuex";
import { useI18n } from "vue-i18n";
import LanguageSwitcher from "@/components/LanguageSwitcher.vue";
import { UserRole } from "@/types/base";
import { ensureNoTrailingSlash } from "@/lib/utils";

const { proxy } = getCurrentInstance();
const store = useStore();
const { locale } = useI18n();

const partnerPageUrl = computed(() => {
  const url = ensureNoTrailingSlash(process.env.VUE_APP_PARTNER_PAGE_ENDPOINT);
  return `${url}/${locale.value}`;
});

// locale에 따른 통화 아이콘 매핑
const currencyIcon = computed(() => {
  const iconMap = {
    ko: 'currency-won-custom', // 한국 원 (직접 스타일)
    en: 'currency-dollar-custom', // 미국 달러 (직접 스타일)
    vi: 'currency-dong-custom', // 베트남 동 (직접 스타일)
    zh: 'currency-yuan-custom', // 중국 위안 (직접 스타일)
    ja: 'currency-yen-custom', // 일본 엔 (직접 스타일)
    mn: 'currency-tugrik-custom', // 몽골 투그릭 (직접 스타일)
  };
  return iconMap[locale.value] || 'bi-currency-exchange'; // 기본값
});

const user = computed(() => store.state.user);

const logout = (e) => {
  e.preventDefault();
  proxy.axios
    .get(proxy.api_logout)
    .then((res) => {
      console.log(res);
      if (res.status === 200) {
        localStorage.clear();
        sessionStorage.clear();
        proxy.$cookies.remove("user");
        window.location.href = "/";
      }
    });
};
</script>

<template>
  <!-- Top Bar -->
  <div class="header-top">
    <div class="container-fluid pe-4">
      <div
        class="header-top--inner d-flex justify-content-between align-items-center flex-wrap"
      >
        <!-- Left Box -->
        <div class="left-box d-none d-lg-flex align-items-center">
          <ul class="info-list">
            <li>
              <a href="javascript:void(0)">
                <span class="icon bi bi-star-fill"></span>
                <span style="color: #98741a">{{ user.username }}{{ $t('header.honorific') }}</span>
                {{ $t('header.announcement') }}
              </a>
            </li>
            <li v-if="user.role !== UserRole.USER">
              <a :href="partnerPageUrl" target="_blank" class="cursor-pointer">
                {{ $t('header.partnerPage') }}
              </a>
            </li>
          </ul>
        </div>

        <!-- Right Box -->
        <div class="right-box d-flex align-items-center">
          <LanguageSwitcher />
        
          <!-- Social Box -->
          <div class="info-list overflow-x-auto" :style="{ width: $isMobile() ? '95%' : 'auto' }">
            <ul class="p-0 m-0">
              <li>
                <a
                  href="javascript:void(0)"
                  @click="
                    $store.dispatch('showHomePopup', { open: true, tab: 'recharege' })
                  "
                >
                  <span class="icon bi bi-flower1"></span>
                  {{ $t('header.chip') }}:
                  <strong>{{
                    new Intl.NumberFormat("en-US", {
                      minimumFractionDigits: 0,
                    }).format(user.chip)
                  }}</strong>
                </a>
              </li>
              <li>
                <a
                  href="javascript:void(0)"
                  @click="
                    store.dispatch('showHomePopup', { open: true, tab: 'pointToChip' })
                  "
                >
                  <span class="icon" :class="currencyIcon"></span>
                  {{ $t('header.point') }}
                  <strong>{{
                    new Intl.NumberFormat("en-US", {
                      minimumFractionDigits: 0,
                    }).format(user.point)
                  }}</strong>
                </a>
              </li>
              <li>
                <a href="javascript:void(0)" @click="logout">
                  <span class="icon bi bi-arrow-right-square-fill"></span>
                  <span style="color: #d70006; font-weight: 700">
                    {{ $t('common.logout') }}
                  </span>
                </a>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
/* 커스텀 통화 아이콘 스타일 */
.currency-won-custom::before {
  content: "₩";
  font-weight: bold;
  font-size: 14px;
}

.currency-dollar-custom::before {
  content: "$";
  font-weight: bold;
  font-size: 14px;
}

.currency-yen-custom::before {
  content: "¥";
  font-weight: bold;
  font-size: 14px;
}

.currency-yuan-custom::before {
  content: "¥";
  font-weight: bold;
  font-size: 14px;
}

.currency-dong-custom::before {
  content: "₫";
  font-weight: bold;
  font-size: 14px;
}

.currency-tugrik-custom::before {
  content: "₮";
  font-weight: bold;
  font-size: 14px;
}

/* Bootstrap Icons와 일관성을 위한 공통 스타일 */
.currency-won-custom,
.currency-dollar-custom,
.currency-yen-custom,
.currency-yuan-custom,
.currency-dong-custom,
.currency-tugrik-custom {
  display: inline-block;
  vertical-align: middle;
  margin-right: 4px;
}
</style>
